import React, { FC, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { HeadProvider, Link, Title } from 'react-head'

import { SignInPage } from '../common/pages/sign-in-page'
import { PasswordResetPage } from '../common/pages/password-reset-page'
import { ResetPasswordRequestPage } from '../common/pages/reset-password-request-page'
import { ResetPasswordRequestSuccessPage } from '../common/pages/reset-password-request-success-page'
import { ResetPasswordRequestErrorPage } from '../common/pages/reset-password-request-error-page'
import { MeasurementDevicePage } from '../common/pages/measurement-device-page'
import { LoadbalancerPage } from '../common/pages/loadbalancer-page'
import { LoadBalancerDetailsWidgetPage } from '../common/components/load-balancer-details-widget-page'
import { ChargePointDetailsPage } from '../common/pages/chargepoint-details-page'
import { ReportsPage } from '../common/pages/reports-page'
import CookieConsentBar from '../common/features/cookie-consent/CookieConsentBar'
import { RFIDTagsPage } from '../common/pages/rfid-tags-page'
import { canAccessBillingPage } from '../lib/entitlementUtils'
import { useEntitlements } from '../common/hooks/useEntitlements'
import { usePortalSession } from '../common/hooks/usePortalSession'
import { PortalSessionLoader } from '../common/components/portal-session-loader'
import type { PortalSessionStatus } from '../common/hooks/portalSessionState'
import { ScopedAccessBanner } from '../common/components/scoped-access-banner'

import { AdminPageHeader } from './components/page-header'
import { RootState } from './redux/store'
import { AcceptInvitationPage } from './pages/accept-invitation-page'
import { DashboardPage } from './pages/dashboard-page'
import { SelectOrganisationPage } from './pages/select-organisation-page'
import { PoolsPage } from './pages/pools-page'
import { PoolPage } from './pages/pool-page'
import { MeasurementDevicesPage } from './pages/measurementdevices-page'
import { UsersPage } from './pages/users-page'
import { UserPoolsPage } from './pages/user-pools-page'
import { UserChargePointsPage } from './pages/user-chargepoints-page'
import { UserNfcTagsPage } from './pages/user-nfc-tags-page'
import { AdminChargePointsPage } from './pages/charge-points-page'
import { SettingsPage } from './pages/settings-page'
import { UserPage } from './pages/user-page'
import { ChangeOperatorPage } from './pages/change-operator-page/change-operator-page'
import { ChangeOperatorSuccessPage } from './pages/change-operator-success-page'
import { BillingPage } from './pages/billing-page'
import { BillingSuccessPage } from './pages/billing-success-page/billing-success-page'
import { BillingFailedPage } from './pages/billing-failed-page/billing-failed-page'

type RequireAuthProps = {
    isHeaderShown?: boolean
    sessionStatus: PortalSessionStatus
}

const RequireAuth: FC<RequireAuthProps> = ({ isHeaderShown = true, sessionStatus }) => {
    const location = useLocation()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    if (sessionStatus === 'loading') {
        return <PortalSessionLoader />
    }

    if (sessionStatus !== 'authenticated') {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />
    }

    if (!companyId && location.pathname !== '/select-organisation') {
        return <Navigate to="/select-organisation" state={{ from: location.pathname }} replace />
    }

    return (
        <div>
            <div className="sticky top-0 z-5">
                <ScopedAccessBanner />
                {isHeaderShown ? (
                    <AdminPageHeader className="static h-5rem bg-white px-4 lg:px-6 pt-5 pb-6 xl:pt-4 xl:pb-4" />
                ) : null}
            </div>
            <Outlet />
        </div>
    )
}

const App = () => {
    const sessionStatus = usePortalSession()
    const selectedCompanyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const entitlements = useEntitlements()
    const billingEnabled = canAccessBillingPage(entitlements)

    const location = useLocation()

    useLayoutEffect(() => {
        // Scroll to the top of the page when the route changes
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }, [location.pathname])

    return (
        <HeadProvider>
            <Title>Charge Amps Admin</Title>
            <Link rel="shortcut icon" href="/common/content/images/favicon-16.png" />
            <Link rel="apple-touch-icon" href="/common/content/images/logo_white_retina.png" />
            <Routes>
                <Route
                    path="/*"
                    element={
                        sessionStatus === 'loading' ? (
                            <PortalSessionLoader />
                        ) : (
                            <Navigate
                                to={
                                    sessionStatus !== 'authenticated'
                                        ? '/login'
                                        : selectedCompanyId
                                          ? '/dashboard'
                                          : '/select-organisation'
                                }
                            />
                        )
                    }
                />
                <Route path="login" element={<SignInPage />} />
                <Route path="reset-password" element={<PasswordResetPage />} />
                <Route path="acceptInvitation" element={<AcceptInvitationPage />} />
                <Route path="reset-password-request" element={<ResetPasswordRequestPage />} />
                <Route path="reset-password-request-success" element={<ResetPasswordRequestSuccessPage />} />
                <Route path="reset-password-request-error" element={<ResetPasswordRequestErrorPage />} />
                <Route element={<RequireAuth isHeaderShown={false} sessionStatus={sessionStatus} />}>
                    <Route path="select-organisation" element={<SelectOrganisationPage />} />
                    <Route path="change-operator/:transferKey" element={<ChangeOperatorPage />} />
                    <Route path="change-operator-success" element={<ChangeOperatorSuccessPage />} />
                </Route>
                <Route element={<RequireAuth sessionStatus={sessionStatus} />}>
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="pools" element={<PoolsPage />} />
                    <Route path="pool/:poolId" element={<PoolPage />} />
                    <Route path="measurementDevices" element={<MeasurementDevicesPage />} />
                    <Route path="measurementDevice/:deviceId" element={<MeasurementDevicePage />} />
                    <Route path="loadbalancer" element={<LoadbalancerPage />} />
                    <Route path="loadbalancer/:id" element={<LoadBalancerDetailsWidgetPage />} />
                    <Route path="nfcTags" element={<RFIDTagsPage />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="user/:id" element={<UserPage />} />
                    <Route path="user/:id/userPools" element={<UserPoolsPage />} />
                    <Route path="user/:id/userChargePoints" element={<UserChargePointsPage />} />
                    <Route path="user/:id/userNfcTags" element={<UserNfcTagsPage />} />
                    <Route path="chargepoints" element={<AdminChargePointsPage />} />
                    <Route path="chargepoints/:chargePointId" element={<ChargePointDetailsPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    {billingEnabled ? <Route path="billing" element={<BillingPage />} /> : null}
                    <Route path="billing/success" element={<BillingSuccessPage />} />
                    <Route path="billing/failed" element={<BillingFailedPage />} />
                </Route>
            </Routes>
            <CookieConsentBar />
        </HeadProvider>
    )
}

export default App
